import type { Price, Tier } from '@shared/dream-components';
import { computeGroupTotalCents, currencyFormatter, DEFAULT_GROUP_MIN_QUANTITY } from '@shared/dream-components';
import type { TFunction } from 'i18next';

import type { UpgradeContextType } from '~/context/types';

interface ActiveSelection {
  tier: Tier | undefined;
  price: Price | undefined;
  setTier: (tier: Tier) => void;
  setPrice: (price: Price) => void;
}

/**
 * Resolves the active tier/price selection (and their setters) for the current
 * form mode. Centralizes the gift / group / individual switch so callers don't
 * have to thread the right pair of fields through nested ternaries.
 */
export const getActiveSelection = (contextValue: UpgradeContextType): ActiveSelection => {
  switch (contextValue.currentFormMode) {
    case 'gift':
      return {
        tier: contextValue.selectedGiftTier,
        price: contextValue.selectedGiftPrice,
        setTier: contextValue.setSelectedGiftTier,
        setPrice: contextValue.setSelectedGiftPrice,
      };
    case 'group':
      return {
        tier: contextValue.selectedGroupTier,
        price: contextValue.selectedGroupPrice,
        setTier: contextValue.setSelectedGroupTier,
        setPrice: contextValue.setSelectedGroupPrice,
      };
    default:
      return {
        tier: contextValue.selectedTier,
        price: contextValue.selectedPrice,
        setTier: contextValue.setSelectedTier,
        setPrice: contextValue.setSelectedPrice,
      };
  }
};

interface GroupSummaryStrings {
  groupSummary: string;
  minimumSeatsLabel: string;
}

/**
 * Derives the localized group-form strings (running total summary + minimum
 * seats helper) from the upgrade context. Pure function so it can be exercised
 * from tests without mounting React.
 */
export const buildGroupSummaryStrings = (
  contextValue: UpgradeContextType,
  t: TFunction,
  language = 'en-US',
): GroupSummaryStrings => {
  const { selectedGroupPrice, selectedGroupTier, seatCount } = contextValue;
  const formatCount = (n: number) => new Intl.NumberFormat(language).format(n);

  const intervalKey = selectedGroupPrice?.interval;
  const intervalTranslationKey = intervalKey ? `upgrade.tabs.group.interval.${intervalKey}` : '';
  const translatedInterval = intervalTranslationKey ? t(intervalTranslationKey) : '';
  const intervalLabel =
    selectedGroupPrice?.denominator ||
    (translatedInterval && translatedInterval !== intervalTranslationKey
      ? translatedInterval
      : (intervalKey?.split('_').join(' ') ?? ''));

  const totalFormatted =
    selectedGroupPrice && selectedGroupTier && seatCount
      ? currencyFormatter({
          value:
            computeGroupTotalCents({
              amountCents: selectedGroupPrice.original_amount_cents,
              quantity: seatCount,
              discountPercent: selectedGroupTier.group_discount_percent,
            }) / 100,
          currency: selectedGroupPrice.currency,
        })
      : '';

  const groupSummary =
    seatCount !== null && seatCount > 0
      ? t('upgrade.tabs.group.form.summary', {
          count: seatCount,
          formattedCount: formatCount(seatCount),
          total: totalFormatted,
          interval: intervalLabel,
        })
      : '';

  const minimumQuantity = selectedGroupTier?.group_minimum_quantity ?? DEFAULT_GROUP_MIN_QUANTITY;
  const minimumSeatsLabel = t('upgrade.tabs.group.form.minimum_seats', {
    count: minimumQuantity,
    formattedCount: formatCount(minimumQuantity),
  });

  return { groupSummary, minimumSeatsLabel };
};
